import {Component, computed, effect, inject, signal} from '@angular/core';
import {finalize} from 'rxjs';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {TranslocoDirective} from '@jsverse/transloco';
import {ProjectBaseStepComponent} from '../../../project-base-step/project-base-step.component';
import {Step13ProjectSettingsService} from './step-1-3.service';
import {ToastService} from '@frontend/toast';
import {ChapterControllerService, ProjectSettingUpsertRequest} from '@frontend/shared-api';
import {Step13ImportanceScaleTabComponent} from './step-1-3-importance-scale-tab/step-1-3-importance-scale-tab.component';
import {Step13NormalizationRangeTabComponent} from './step-1-3-normalization-range-tab/step-1-3-normalization-range-tab.component';
import {Step13HostingProviderTabComponent} from './step-1-3-hosting-provider-tab/step-1-3-hosting-provider-tab.component';
import {Step13ProjectLanguageTabComponent} from './step-1-3-project-language-tab/step-1-3-project-language-tab.component';
import {TabStatusEnum} from '../../../../../../shared/tabs/tab-status.model';
import {TabStatusDirective} from '../../../../../../shared/tabs/tab-status.directive';
import {LoadingComponent} from '../../../../../../shared/components/loading/loading.component';
import {TabsUnsavedRevealDirective} from '../../../../../../shared/tabs/tabs-unsaved-reveal.directive';

type ScaleType = 'linear' | 'exp';

interface NormalizationValues {
  maxPriceToGetPoints?: number;
}

@Component({
  selector: 'app-step-1-3-project-settings',
  imports: [
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    TranslocoDirective,
    Step13ImportanceScaleTabComponent,
    Step13NormalizationRangeTabComponent,
    Step13HostingProviderTabComponent,
    Step13ProjectLanguageTabComponent,
    LoadingComponent,
    TabStatusDirective,
    TabsUnsavedRevealDirective,
  ],
  templateUrl: './step-1-3-project-settings.component.html',
})
export class Step13ProjectSettingsComponent extends ProjectBaseStepComponent {
  private readonly settingsService = inject(Step13ProjectSettingsService);
  private readonly toast = inject(ToastService);
  private readonly chapterController = inject(ChapterControllerService);

  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly selectedScale = signal<ScaleType>('linear');
  readonly originalScale = signal<ScaleType | null>(null);
  readonly originalNormalizationValues = signal<NormalizationValues | null>(null);
  readonly originalHostingLocation = signal<string | null>(null);
  readonly originalProjectLanguage = signal<string | null>(null);

  private getScaleStatus(): TabStatusEnum {
    const original = this.originalScale();
    const selected = this.selectedScale();

    if (original === null) return TabStatusEnum.NOT_STARTED;
    if (original !== selected) return TabStatusEnum.IN_PROGRESS;
    return TabStatusEnum.COMPLETED;
  }

  readonly tabStatuses = computed(() => ({
    scale: this.getScaleStatus(),
    normalization: this.originalNormalizationValues()
      ? TabStatusEnum.COMPLETED
      : TabStatusEnum.NOT_STARTED,
    hostingProvider: this.originalHostingLocation()
      ? TabStatusEnum.COMPLETED
      : TabStatusEnum.NOT_STARTED,
    projectLanguage: this.originalProjectLanguage()
      ? TabStatusEnum.COMPLETED
      : TabStatusEnum.NOT_STARTED,
  }));

  constructor() {
    super();

    effect(() => {
      this.initializeProjectSettings();
    });
  }

  private initializeProjectSettings(): void {
    const projectId = this.projectId;
    if (!projectId) return;

    this.loadSettings(projectId).subscribe((settings) => {
      if (!settings) return;

      const scaleValue: ScaleType = settings.isExponential ? 'exp' : 'linear';
      this.selectedScale.set(scaleValue);
      this.originalScale.set(settings.isExponential === null ? null : scaleValue);

      this.originalNormalizationValues.set(
        settings.maxPriceToGetPoints ? {maxPriceToGetPoints: settings.maxPriceToGetPoints} : null,
      );

      this.originalHostingLocation.set(settings.hostingProviderLocation ?? null);

      this.originalProjectLanguage.set(settings.projectLanguage ?? null);
    });
  }

  private loadSettings(projectId: number) {
    this.isLoading.set(true);
    return this.settingsService
      .getProjectSettings(projectId)
      .pipe(finalize(() => this.isLoading.set(false)));
  }

  private saveSettings(partialSettings: Partial<ProjectSettingUpsertRequest>): void {
    const projectId = this.projectId;
    if (!projectId) return;

    this.isSaving.set(true);

    this.settingsService
      .upsertProjectSettings(projectId, {...partialSettings})
      .pipe(finalize(() => this.isSaving.set(false)))
      .subscribe({
        next: () => {
          if (partialSettings.isExponential !== undefined) {
            this.originalScale.set(partialSettings.isExponential ? 'exp' : 'linear');
          }

          if (partialSettings.maxPriceToGetPoints !== undefined) {
            this.originalNormalizationValues.set({
              ...this.originalNormalizationValues(),
              maxPriceToGetPoints: partialSettings.maxPriceToGetPoints,
            });
          }

          if (partialSettings.hostingProviderLocation !== undefined) {
            this.originalHostingLocation.set(partialSettings.hostingProviderLocation);
          }

          if (partialSettings.projectLanguage !== undefined) {
            this.originalProjectLanguage.set(partialSettings.projectLanguage);
          }

          this.toast.showSuccess('common.success', 'project.steps.sectionSaved');
          this.stepState.loadStepStatuses(projectId);
        },
      });
  }

  onSubmitScale(): void {
    this.saveSettings({isExponential: this.selectedScale() === 'exp'});
  }

  onScaleChange(scale: ScaleType): void {
    this.selectedScale.set(scale);
  }

  onSubmitNormalizationRange(values: NormalizationValues): void {
    this.saveSettings(values);
  }

  onSubmitHostingLocation(values: {hostingProviderLocation: string}): void {
    this.saveSettings(values);
  }

  onSubmitProjectLanguage(values: {projectLanguage: string}): void {
    this.saveSettings(values);
  }
}
